import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { photographerAPI } from '@services/photographerApi';
import { getStorageItem, setStorageItem } from '@utils/helpers';
import type {
  Photographer,
  PhotographerSubscription,
  PhotographerContextType,
} from '@/types/photographer';

interface PhotographerState {
  photographer: Photographer | null;
  subscription: PhotographerSubscription | null;
  loading: boolean;
  error: string | null;
}

type PhotographerAction =
  | { type: 'FETCH_START' }
  | {
      type: 'FETCH_SUCCESS';
      payload: { photographer: Photographer; subscription?: PhotographerSubscription };
    }
  | { type: 'FETCH_FAILURE'; payload: string }
  | { type: 'UPDATE_PHOTOGRAPHER'; payload: Partial<Photographer> }
  | { type: 'UPDATE_SUBSCRIPTION'; payload: PhotographerSubscription }
  | { type: 'CLEAR_ERROR' };

const initialState: PhotographerState = {
  photographer: null,
  subscription: null,
  loading: false,
  error: null,
};

const photographerReducer = (
  state: PhotographerState,
  action: PhotographerAction
): PhotographerState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        photographer: action.payload.photographer,
        subscription: action.payload.subscription || state.subscription,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case 'UPDATE_PHOTOGRAPHER':
      return {
        ...state,
        photographer: state.photographer ? { ...state.photographer, ...action.payload } : null,
      };

    case 'UPDATE_SUBSCRIPTION':
      return {
        ...state,
        subscription: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

const PhotographerContext = createContext<PhotographerContextType | null>(null);

interface PhotographerProviderProps {
  children: ReactNode;
}

export const PhotographerProvider: React.FC<PhotographerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(photographerReducer, initialState);

  // Initialize photographer data from localStorage or fetch
  useEffect(() => {
    const initializePhotographer = async () => {
      const cachedPhotographer = getStorageItem('photographer_data', null);
      if (cachedPhotographer) {
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { photographer: cachedPhotographer },
        });
      }

      // Always fetch fresh data
      await fetchPhotographer();
    };

    initializePhotographer();
  }, []);

  const fetchPhotographer = async (): Promise<void> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const response = await photographerAPI.getProfile();
      const photographer = response.data;

      // Cache photographer data
      setStorageItem('photographer_data', photographer);

      // Subscription state is managed by BillingContext via the canonical
      // /subscriptions API; not loaded here.
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: { photographer },
      });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to fetch photographer data',
      });
      throw error;
    }
  };

  const updatePhotographer = async (updates: Partial<Photographer>): Promise<void> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const response = await photographerAPI.updateProfile(updates);
      const updatedPhotographer = response.data;

      // Update cache
      setStorageItem('photographer_data', updatedPhotographer);

      dispatch({
        type: 'UPDATE_PHOTOGRAPHER',
        payload: updatedPhotographer,
      });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update photographer profile',
      });
      throw error;
    }
  };

  const updateBrandingSettings = async (
    settings: Partial<Photographer['brandingSettings']>
  ): Promise<void> => {
    if (!state.photographer) {
      throw new Error('No photographer profile loaded');
    }

    const updates = {
      brandingSettings: {
        ...state.photographer.brandingSettings,
        ...settings,
      },
    };

    await updatePhotographer(updates);
  };

  const updateBusinessSettings = async (
    settings: Partial<Photographer['settings']>
  ): Promise<void> => {
    if (!state.photographer) {
      throw new Error('No photographer profile loaded');
    }

    const updates = {
      settings: {
        ...state.photographer.settings,
        ...settings,
      },
    };

    await updatePhotographer(updates);
  };

  const value: PhotographerContextType = {
    ...state,
    fetchPhotographer,
    updatePhotographer,
    updateBrandingSettings,
    updateBusinessSettings,
  };

  return <PhotographerContext.Provider value={value}>{children}</PhotographerContext.Provider>;
};

export const usePhotographer = (): PhotographerContextType => {
  const context = useContext(PhotographerContext);
  if (!context) {
    throw new Error('usePhotographer must be used within PhotographerProvider');
  }
  return context;
};
